import { PropsWithChildren } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import palette from '@/theme/palette.ts';

type DialogStyledProps = PropsWithChildren<{
  title: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: () => void;
  onActionButtonClick?: () => void;
  onSecondaryActionButtonClick?: () => void;
  actionButtonLabel?: string;
  secondaryActionButtonLabel?: string;
  maxWidth?: Breakpoint;
  isCorrectlyFilledOut?: boolean;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
}>;

export const DialogStyled = ({
  title,
  open,
  setOpen,
  onClose,
  onActionButtonClick,
  onSecondaryActionButtonClick,
  actionButtonLabel,
  secondaryActionButtonLabel,
  maxWidth,
  isCorrectlyFilledOut = true,
  isLoading = false,
  children,
  sx,
}: DialogStyledProps) => {
  const handleClose =
    onClose ||
    (() => {
      setOpen(false);
    });
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth || 'sm'}
      fullWidth
      sx={{ '.MuiPaper-root': { borderRadius: '10px' }, ...sx }}
    >
      <DialogTitle>
        <Box sx={{ borderBottom: `1px solid ${palette.border.default}`, pb: 2 }}>{title}</Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: 'absolute', right: 8, top: 12 }}
      >
        <span className="material-symbols-rounded">close</span>
      </IconButton>
      {!isCorrectlyFilledOut && (
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{
            marginX: 3,
            padding: '16px',
            gap: '10px',
            borderRadius: '4px',
            background: palette.toast.error,
          }}
        >
          <ErrorOutlineIcon fontSize="inherit" sx={{ color: palette.surface.white }} />
          <Typography variant="body2" sx={{ color: palette.text.contrast }}>
            Vyplňte správně všechny potřebné údaje
          </Typography>
        </Stack>
      )}
      {children && <DialogContent>{children}</DialogContent>}

      {children && <Box sx={{ borderTop: `1px solid ${palette.border.default}`, mx: 3 }} />}
      <DialogActions>
        <Stack
          direction="row"
          justifyContent={onSecondaryActionButtonClick ? 'space-between' : 'end'}
          width="100%"
          p={3}
          gap={2}
        >
          {onSecondaryActionButtonClick && (
            <Button
              variant="text"
              sx={{ color: palette.primary.main }}
              onClick={onSecondaryActionButtonClick}
            >
              {secondaryActionButtonLabel || 'Smazat'}
            </Button>
          )}
          {onActionButtonClick && (
            <Button
              variant="contained"
              onClick={onActionButtonClick}
              disabled={isLoading || !isCorrectlyFilledOut}
              sx={{ alignSelf: 'flex-end' }}
            >
              {actionButtonLabel || 'Přidat'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
